/**
 * Append the calendar year to every month label as a two-digit "'YY" suffix,
 * for timeframes whose column prefix follows the "FY26"-style fiscal-year
 * convention. The fiscal year runs Nov → Oct, so a single FY spans two
 * calendar years:
 *   - Nov & Dec belong to the PRIOR calendar year (FY26 Nov/Dec → 2025)
 *   - Jan … Oct match the FY number (FY26 Jan…Oct → 2026)
 * Showing the year on all months (e.g. "FY26 Nov'25", "FY26 Jan'26") makes the
 * real calendar year obvious and keeps every month visually consistent.
 *
 * Multi-tenant timeframe prefixes are admin-configurable free text (e.g.
 * "2026", "Sprint", "FY26") — the Nov→prior-year rule only makes sense for an
 * actual "FY<number>" prefix, so anything else (including a plain calendar
 * year like "2026 Jan") is returned unchanged rather than getting a redundant
 * or incorrect suffix appended.
 *
 * The month is always the last token, so this works for plain month labels
 * ("FY26 Dec") and quarterly-grouped labels ("FY26 Q1 Dec") alike. Pure quarter
 * labels ("FY26 Q1") have no month token and are returned unchanged, so this is
 * safe to call on any label.
 */
const MONTH_TOKENS = new Set([
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]);

const FY_PREFIX = /^FY(\d{2,4})$/i;

export function formatMonthLabel(fullLabel: string): string {
  const parts = fullLabel.split(' ');
  if (parts.length < 2) return fullLabel;
  const monthTok = parts[parts.length - 1];
  if (!MONTH_TOKENS.has(monthTok)) return fullLabel;
  const fyMatch = parts[0].match(FY_PREFIX);
  if (!fyMatch) return fullLabel;
  const fyNum = parseInt(fyMatch[1], 10);
  // Nov/Dec fall in the calendar year before the FY number (FY26 → 2025);
  // Jan–Oct match the FY number (FY26 → 2026).
  const calYear = monthTok === 'Nov' || monthTok === 'Dec' ? fyNum - 1 : fyNum;
  const yy = String((calYear + 100) % 100).padStart(2, '0');
  return `${fullLabel}'${yy}`;
}
